import React, { useState, useEffect } from "react";
import { ref, onValue, get } from "firebase/database";
import { auth, db } from "../firebaseConfig";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchWorkspaces = async () => {
      try {
        const workspacesRef = ref(db, "neoslide/workspaces");
        const snapshot = await get(workspacesRef);
        if (snapshot.exists()) {
          const workspacesData = snapshot.val();
          const workspacesArray = Object.keys(workspacesData).map((key) => ({
            id: key,
            name: workspacesData[key],
          }));
          setWorkspaces(workspacesArray);
        }
      } catch (error) {
        console.error("Error fetching workspaces:", error);
      }
    };

    fetchWorkspaces();
    const user = auth.currentUser;

    if (!user) {
      console.error("User not authenticated");
      return;
    }

    const usersRef = ref(db, "neoslide/users");
    onValue(usersRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const usersArray = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        setUsers(usersArray);
      }
    });
  }, []);

  const validateFields = () => {
    let newErrors = {};

    if (!editingUser || typeof editingUser !== "object") {
      setErrors({ general: "User data is missing" });
      return false;
    }

    if (!editingUser.name?.trim()) newErrors.name = "Name is required";
    if (!editingUser.surname?.trim()) newErrors.surname = "Surname is required";
    if (!editingUser.role) newErrors.role = "Role is required";
    if (!editingUser.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(editingUser.email)) {
      newErrors.email = "Invalid email format";
    }
    if (!editingUser.id && !editingUser.password?.trim()) {
      newErrors.password = "Password is required";
    } else if (editingUser.password && editingUser.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters long";
    }
    if (editingUser.status === undefined || editingUser.status === null) {
      newErrors.status = "Status is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDelete = (userId) => {
    setUserToDelete(userId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteConfirm(false);
    try {
      const user = auth.currentUser;
      if (!user) {
        setMessage("You must be logged in to perform this action.");
        return;
      }

      const token = await user.getIdToken();
      const response = await fetch(
        `https://us-central1-neods-c3270.cloudfunctions.net/createUser/api/users/${userToDelete}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();

      if (result.success) {
        setMessage("User deleted successfully");
        setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userToDelete));
      } else {
        setMessage("Error deleting user: " + result.message);
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      setMessage("Error deleting user");
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setShowPopup(true);
  };

  const handleAddUser = () => {
    if (!selectedWorkspace) {
      setMessage("Please select a workspace first!");
      return;
    }
    setEditingUser({
      id: "",
      name: "",
      surname: "",
      email: "",
      password: "",
      mobile: "",
      address: "",
      role: "",
      workspaceId: selectedWorkspace,
      status: 1,
      appVersion: '0.0'
    });
    setShowPopup(true);
  };

  const handleChange = (e) => {
    setEditingUser({ ...editingUser, [e.target.name]: e.target.value });
    setMessage("");
  };

  const handleSubmit = async () => {
    if (!validateFields()) return;

    try {
      const user = auth.currentUser;
      if (!user) {
        setMessage("You must be logged in to perform this action.");
        return;
      }

      const token = await user.getIdToken();
      const url = editingUser.id
        ? `https://us-central1-neods-c3270.cloudfunctions.net/createUser/api/users/${editingUser.id}`
        : "https://us-central1-neods-c3270.cloudfunctions.net/createUser/api/users";
      const method = editingUser.id ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editingUser),
      });

      const result = await response.json();

      if (result.success) {
        setMessage(editingUser.id ? "User updated successfully" : "User added successfully");
        setShowPopup(false);
      } else {
        setMessage("Error: " + result.message);
        setShowPopup(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setMessage("Error: " + error.message);
      setShowPopup(false);
    }
  };

  return (
    <div className="workspaces-container">
      <h2>Users</h2>
      <button className="workspaces-container__button" onClick={handleAddUser}>
        + Add User
      </button>
      <select
        id="workspaceSelect"
        value={selectedWorkspace}
        onChange={(e) => {
          setMessage("");
          setSelectedWorkspace(e.target.value);
        }}
      >
        <option value="">-- Select Workspace --</option>
        {workspaces.map((workspace) => (
          <option key={workspace.id} value={workspace.name}>
            {workspace.name}
          </option>
        ))}
      </select>
      {message && <h2 className="success-message">{message}</h2>}
      {selectedWorkspace && (
        <table className="devices-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Surname</th>
              <th>Role</th>
              <th>Edit</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {users
              .filter((user) => user.workspaceId === selectedWorkspace)
              .map((user) => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td>{user.surname}</td>
                  <td>{user.role}</td>
                  <td>
                    <button className="edit-btn" onClick={() => handleEdit(user)}>
                      Edit
                    </button>
                  </td>
                  <td>
                    <button className="delete-btn" onClick={() => handleDelete(user.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
      {showPopup && editingUser && (
        <div className="edit-popup">
          <div className="edit-content">
            <h3>{editingUser.id ? "Edit User" : "Add User"}</h3>
            <label>Name:</label>
            <input type="text" name="name" value={editingUser.name} onChange={handleChange} />
            {errors.name && <span className="error-message">{errors.name}</span>}
            <label>Surname:</label>
            <input type="text" name="surname" value={editingUser.surname} onChange={handleChange} />
            {errors.surname && <span className="error-message">{errors.surname}</span>}
            <label>Email:</label>
            <input
              type="text"
              name="email"
              value={editingUser.email}
              onChange={handleChange}
              disabled={!!editingUser.id}
              autoComplete="new-email"
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
            {!editingUser.id && (
              <>
                <label>Password:</label>
                <input type="password" name="password" onChange={handleChange} autoComplete="new-password" />
                {errors.password && <span className="error-message">{errors.password}</span>}
              </>
            )}
            <label>Mobile:</label>
            <input type="text" name="mobile" value={editingUser.mobile} onChange={handleChange} />
            {errors.mobile && <span className="error-message">{errors.mobile}</span>}
            <label>Address:</label>
            <input type="text" name="address" value={editingUser.address} onChange={handleChange} />
            <label>Role:</label>
            <select name="role" value={editingUser.role} onChange={handleChange}>
              <option value="">-- Select Role --</option>
              <option value="admin">Admin</option>
              <option value="superadmin">Super Admin</option>
              <option value="device">Device</option>
              <option value="viewer">Viewer</option>
            </select>
            {errors.role && <span className="error-message">{errors.role}</span>}
            <label>Status:</label>
            <input
              type="number"
              name="status"
              value={editingUser.status}
              onChange={handleChange}
            />
            <label>appVersion:</label>
            <input
              type="text"
              name="appVersion"
              value={editingUser.appVersion}
              onChange={handleChange}
            />
            <label>Workspace ID:</label>
            <input type="text" name="workspaceId" value={editingUser.workspaceId} disabled />
            <div className="buttons">
              <button className="popup__button" onClick={handleSubmit}>
                Save
              </button>
              <button
                className="popup__button-cancel"
                onClick={() => {
                  setShowPopup(false);
                  setErrors({});
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showDeleteConfirm && (
        <div className="delete-popup">
          <div className="delete-content">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this user?</p>
            <div className="buttons">
              <button className="popup__button" onClick={handleDeleteConfirm}>
                Yes
              </button>
              <button className="popup__button-cancel" onClick={handleDeleteCancel}>
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;